import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loader2, MailCheck, ArrowRight, Stethoscope } from "lucide-react";

export default function ComingSoonPage({ country, countryCode }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const isCanada = (countryCode || "").toUpperCase() === "CA";
  const place = isCanada ? "Canada" : (country && country !== "Unknown" ? country : "your region");

  const notify = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email: email.trim(), source: "coming_soon" });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paw-bg flex flex-col">
      <header className="px-5 sm:px-8 py-5">
        <img src="/pawlinx-logo-nav.png" alt="PawLinx" className="h-8 w-auto" />
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-paw-primary/10 text-paw-primary px-4 py-1.5 font-manrope text-[13px] font-semibold mb-6">
            <Stethoscope className="w-4 h-4" /> Coming soon
          </span>

          <h1 className="font-outfit font-extrabold text-3xl sm:text-[40px] leading-tight text-paw-text tracking-tight">
            We're launching in {place} soon
          </h1>

          <p className="font-manrope text-[15px] sm:text-base text-paw-text-muted mt-4 leading-relaxed max-w-md mx-auto">
            PawLinx affordable pet medication is currently available to customers in the
            United States. We're working hard to bring it to {place}. Leave your email and
            we'll let you know the moment we go live near you.
          </p>

          {done ? (
            <div className="mt-8 inline-flex flex-col items-center gap-3 rounded-2xl bg-white border border-paw-border px-8 py-7" data-testid="coming-soon-done">
              <div className="w-12 h-12 rounded-full bg-paw-success/10 flex items-center justify-center">
                <MailCheck className="w-6 h-6 text-paw-success" strokeWidth={2} />
              </div>
              <p className="font-outfit font-bold text-lg text-paw-text">You're on the list!</p>
              <p className="font-manrope text-sm text-paw-text-muted">We'll email you the moment PawLinx launches in {place}.</p>
            </div>
          ) : (
            <form onSubmit={notify} className="mt-8 max-w-md mx-auto" data-testid="coming-soon-form">
              <div className="flex flex-col sm:flex-row gap-2.5">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="flex-1 h-12 px-4 rounded-xl border border-paw-border bg-white text-sm font-manrope focus:outline-none focus:border-paw-primary"
                  data-testid="coming-soon-email"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="h-12 px-6 rounded-xl bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-60 text-white font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                  data-testid="coming-soon-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Notify me <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
              {error && <p className="mt-2 font-manrope text-[13px] text-paw-error">{error}</p>}
              <p className="mt-3 font-manrope text-[12px] text-paw-text-muted">No spam, just a single heads-up when we launch.</p>
            </form>
          )}
        </div>
      </main>

      <footer className="px-5 py-6 text-center font-manrope text-[12px] text-paw-text-muted">
        © {new Date().getFullYear()} PawLinx. In partnership with licensed pharmacies and veterinarians.
      </footer>
    </div>
  );
}
